import React, { useEffect, useState } from 'react';
import { BASE_URL } from '../config/api'; // Assuming BASE_URL is correctly defined
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf'; // Import jsPDF
import 'jspdf-autotable'; // Import for table support
import * as XLSX from 'xlsx'; // Import xlsx for Excel export

const WatchmenList = () => {
    const [watchmen, setWatchmen] = useState([]);
    const [filteredWatchmen, setFilteredWatchmen] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const navigate = useNavigate();

    // Fetch watchmen data when the component mounts
    useEffect(() => {
        fetch(`${BASE_URL}watchman`)
            .then(response => response.json())
            .then(data => {
                if (data.status === 1) {
                    setWatchmen(data.data); // Set initial watchmen data
                    setFilteredWatchmen(data.data); // Set initial filtered watchmen
                } else {
                    console.error('Failed to fetch watchmen:', data.message);
                }
            })
            .catch(error => console.error('Error fetching watchmen:', error));
    }, []);

    // Handle search term change
    const handleSearchChange = (event) => {
        const term = event.target.value;
        setSearchTerm(term);
        filterWatchmen(term, startDate, endDate);
    };

    // Filter watchmen based on the search term and date range
    const filterWatchmen = (term, start, end) => {
        let filtered = watchmen.filter(watchman => {
            // Filter by search term (name, purpose, or contact number)
            const matchesSearch = 
                (watchman.name && watchman.name.toLowerCase().includes(term.toLowerCase())) ||
                (watchman.purpose && watchman.purpose.toLowerCase().includes(term.toLowerCase())) ||
                (watchman.contact_no && watchman.contact_no.includes(term));

            // Convert watchman's time to YYYY-MM-DD format to avoid time part interfering
            const watchmanDate = new Date(watchman.time).toISOString().split('T')[0]; // Format as YYYY-MM-DD

            // Ensure start and end dates are valid and compare dates correctly
            const matchesDateRange = (
                (!start || new Date(watchmanDate) >= new Date(start)) && 
                (!end || new Date(watchmanDate) <= new Date(end))
            );

            // Return only those watchmen that match both the search term and date range
            return matchesSearch && matchesDateRange;
        });

        setFilteredWatchmen(filtered);
    };

    // Handle date range change
    const handleDateChange = () => {
        filterWatchmen(searchTerm, startDate, endDate);
    };

    // Navigate to the edit watchman page
    const handleEdit = (id) => {
        navigate(`/EditWatchman/${id}`);
    };

    // Handle watchman deletion
    const handleDelete = (id) => {
        if (window.confirm('Are you sure you want to delete this watchman?')) {
            fetch(`${BASE_URL}watchman/${id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                },
            })
                .then(response => response.json())
                .then(data => {
                    if (data.status === 1) {
                        // Remove the deleted watchman from both lists
                        setWatchmen(watchmen.filter(watchman => watchman.id !== id));
                        setFilteredWatchmen(filteredWatchmen.filter(watchman => watchman.id !== id));
                    } else {
                        alert('Failed to delete watchman.');
                    }
                })
                .catch(error => console.error('Error deleting watchman:', error));
        }
    };

    // Navigate to the Add Watchman page
    const handleAdd = () => {
        navigate('/AddWatchman');
    };

    // Export filteredWatchmen to PDF
    const handleExportPDF = () => {
        const doc = new jsPDF();

        // Add Title
        doc.text('Watchmen List', 14, 10); // Title at top of PDF

        // Prepare table data for PDF
        const tableData = filteredWatchmen.map(watchman => [
            watchman.name || 'N/A',
            watchman.contact_no || 'N/A',
            watchman.purpose || 'N/A',
            new Date(watchman.time).toLocaleString() || 'N/A',
        ]);

        // Add table to PDF with autoTable
        doc.autoTable({
            head: [['Name', 'Contact No', 'Purpose', 'Time']], // Table headers
            body: tableData,
        });

        // Save the PDF with a filename
        doc.save('watchmen_list.pdf');
    };

    // Export filteredWatchmen to Excel
    const handleExportExcel = () => {
        // Prepare table data for Excel
        const tableData = filteredWatchmen.map(watchman => ({
            Name: watchman.name || 'N/A',
            'Contact No': watchman.contact_no || 'N/A',
            Purpose: watchman.purpose || 'N/A',
            Time: new Date(watchman.time).toLocaleString() || 'N/A',
        }));

        // Create a new workbook
        const ws = XLSX.utils.json_to_sheet(tableData); // Convert JSON data to a worksheet
        const wb = XLSX.utils.book_new(); // Create a new workbook
        XLSX.utils.book_append_sheet(wb, ws, 'Watchmen List'); // Append the worksheet to the workbook

        // Export the workbook to an Excel file
        XLSX.writeFile(wb, 'watchmen_list.xlsx');
    };

    return (
        <div id="main">
            <header className="mb-3">
                <a href="#" className="burger-btn d-block d-xl-none">
                    <i className="bi bi-justify fs-3"></i>
                </a>
            </header>

            <div className="page-heading">
                <div className="page-title">
                    <div className="row">
                        <div className="col-12 col-md-6 order-md-1 order-last">
                            <h3>Watchman List</h3>
                        </div>
                    </div>
                </div>
            </div>

            {/* Watchmen Table */}
            <section className="section">
                <div className="row" id="basic-table">
                    <div className="col-12 col-md-12">
                        <div className="card">
                            <div className="card-header">
                                <div className="row">
                                    <div className="col-md-6">
                                        {/* You can add any extra content here */}
                                    </div>
                                    <div className="col-md-6 text-end">
                                        {/* <button className="btn btn-primary" onClick={handleAdd}>Add Entry</button> */}
                                        <button className="btn btn-secondary ms-2" onClick={handleExportPDF}>Export to PDF</button>
                                        {/* <button className="btn btn-success ms-2" onClick={handleExportExcel}>Export to Excel</button> */}
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                {/* Search and Date Filter Section */}
                                <div className="row mb-3">
                                    <div className="col-md-4">
                                        <label htmlFor="searchTerm" className="form-label">Search by Name, Purpose, or Contact</label>
                                        <input
                                            type="text"
                                            id="searchTerm"
                                            className="form-control"
                                            placeholder="Search by watchman name, purpose, or contact number"
                                            value={searchTerm}
                                            onChange={handleSearchChange}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label htmlFor="startDate" className="form-label">Start Date</label>
                                        <input
                                            type="date"
                                            id="startDate"
                                            className="form-control"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <label htmlFor="endDate" className="form-label">End Date</label>
                                        <input
                                            type="date"
                                            id="endDate"
                                            className="form-control"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                        />
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <button className="btn btn-secondary" onClick={handleDateChange}>Apply Date Filter</button>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-lg">
                                        <thead>
                                            <tr>
                                                <th>NAME</th>
                                                <th>CONTACT NO</th>
                                                <th>PURPOSE</th>
                                                <th className="text-end">ACTION</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredWatchmen.length > 0 ? (
                                                filteredWatchmen.map(watchman => (
                                                    <tr key={watchman.id}>
                                                        <td>{watchman.name || 'N/A'}</td>
                                                        <td>{watchman.contact_no || 'N/A'}</td>
                                                        <td>{watchman.purpose || 'N/A'}</td>
                                                        <td className="text-end">
                                                            {/* <button className="btn btn-success" onClick={() => handleEdit(watchman.id)}>Edit</button> */}
                                                            <button className="btn btn-danger ms-2" onClick={() => handleDelete(watchman.id)}>Delete</button>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="4" className="text-center">No watchmen found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            {/* Watchmen Table End */}
        </div>
    );
};

export default WatchmenList;
